import { ApiError, authApi } from '@milliswift/api-client'
import { getApiErrorMessage } from '@milliswift/auth'
import {
  Button,
  Callout,
  Field,
  Input,
  PasswordStrength,
  useToast,
} from '@milliswift/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { CheckCircle2, Mail } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { AuthCard, AuthPage } from '../components/AuthCard'
import { signupSchema, type SignupValues } from '../lib/schemas'

/**
 * Signup. On success the account exists but is unverified — the user is shown
 * a confirmation screen rather than being signed in, because that is what the
 * platform does: no token is issued until the email is verified.
 */
export default function Signup({ postLoginTarget }: { postLoginTarget: string }) {
  const toast = useToast()
  const [done, setDone] = useState<string | null>(null)

  const form = useForm<SignupValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: { email: '', password: '', full_name: '', company_name: '' },
  })

  const password = form.watch('password')
  const submit = form.handleSubmit(async (values) => {
    try {
      const result = await authApi.signup(values)
      setDone(result.account_id)
    } catch (error) {
      const apiError = ApiError.from(error)

      // The server's field-level details map straight onto our form fields.
      if (apiError.code === 'validation_error' || apiError.status === 400) {
        const details = (apiError as ApiError & { details?: Record<string, unknown> }).details
        if (details && typeof details === 'object') {
          for (const [field, problem] of Object.entries(details)) {
            if (field in values) {
              form.setError(field as keyof SignupValues, {
                message: typeof problem === 'string' ? problem : 'Check this field',
              })
            }
          }
          return
        }
      }

      if (apiError.status === 409) {
        form.setError('email', { message: 'An account with this email already exists.' })
        return
      }

      toast.error('Could not create your account', getApiErrorMessage(error))
    }
  })

  if (done) {
    return (
      <AuthPage>
        <AuthCard title="Check your email">
          <div className="flex flex-col items-center gap-4 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-success-soft">
              <CheckCircle2 className="h-6 w-6 text-success-text" aria-hidden />
            </span>
            <p className="text-sm text-muted">
              We sent a verification link to <strong className="text-foreground">{form.getValues('email')}</strong>.
              Click it to activate your account, then sign in.
            </p>
            <Button className="mt-2 w-full" onClick={() => (window.location.href = postLoginTarget)}>
              Continue to sign in
            </Button>
            <p className="text-caption text-subtle">
              Wrong address or nothing arrived?{' '}
              <Link to="/login" className="text-accent underline-offset-4 hover:underline">
                Start over
              </Link>
            </p>
          </div>
        </AuthCard>
      </AuthPage>
    )
  }

  return (
    <AuthPage>
      <AuthCard
        title="Create your account"
        subtitle="Free tier: 1B rows scanned every month, no card required."
        footer={
          <>
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-accent underline-offset-4 hover:underline">
              Sign in
            </Link>
          </>
        }
      >
        <form onSubmit={submit} className="space-y-4" noValidate>
          <Field label="Full name" required error={form.formState.errors.full_name?.message}>
            <Input
              type="text"
              autoComplete="name"
              placeholder="Ada Lovelace"
              {...form.register('full_name')}
            />
          </Field>

          <Field label="Work email" required error={form.formState.errors.email?.message}>
            <Input
              type="email"
              autoComplete="email"
              placeholder="you@company.com"
              leading={<Mail className="h-4 w-4" />}
              {...form.register('email')}
            />
          </Field>

          <Field label="Company" hint="Optional — appears on invoices." error={form.formState.errors.company_name?.message}>
            <Input type="text" autoComplete="organization" placeholder="Acme Inc." {...form.register('company_name')} />
          </Field>

          <Field
            label="Password"
            required
            hint="At least 12 characters, with upper, lower, a number and a symbol."
            error={form.formState.errors.password?.message}
          >
            <Input type="password" autoComplete="new-password" placeholder="••••••••••••" {...form.register('password')} />
          </Field>

          {password ? <PasswordStrength password={password} /> : null}

          <Button type="submit" className="w-full" loading={form.formState.isSubmitting}>
            Create account
          </Button>

          <Callout tone="neutral" className="text-left">
            By creating an account you agree to the Terms of Service and Privacy Policy.
          </Callout>
        </form>
      </AuthCard>
    </AuthPage>
  )
}
